import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';

import { select, NgRedux } from '@angular-redux/store';

import { ApplicationFacade, NotificationFacade } from '@ridango/components';
import {
    RegisterWithEmailDTO,
    WidgetOptionsForEmailRegister,
    valueMatchValidator,
    FEATURE_FLAGS,
    BaseComponent
} from '@ridango/core';

import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { TermsDialogMobileComponent } from '@ridango/components/lib/core-ui/advanced/terms-dialog-mobile/terms-dialog-mobile.component';

import Env from '@ridango/core/env';
import { passwordValidator } from '@ridango/core/lib/validators/password.validator';
import { phoneValidNumberValidator } from '@ridango/core/lib/validators/phone.validator';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { SavePasswordService } from '@ridango/core/lib/services/save-password.service';
import { KeyboardAssistService } from '../../../../services/keyboard-assist.service';
import { LaunchDarklyService } from '@ridango/feature-flags';

@Component({
    selector: 'rwidget-signup-email-form',
    templateUrl: './signup-email-form.component.html',
    styleUrls: ['./signup-email-form.component.scss']
})
export class SignupEmailFormComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('EMAIL') EMAIL: TemplateRef<unknown>;
    @ViewChild('PHONE_NUMBER') PHONE_NUMBER: TemplateRef<unknown>;
    @ViewChild('PERSONAL_CODE') PERSONAL_CODE: TemplateRef<unknown>;
    @ViewChild('FIRST_NAME') FIRST_NAME: TemplateRef<unknown>;
    @ViewChild('LAST_NAME') LAST_NAME: TemplateRef<unknown>;
    @ViewChild('PASSWORD') PASSWORD: TemplateRef<unknown>;
    @ViewChild('CONFIRM_PASSWORD') CONFIRM_PASSWORD: TemplateRef<unknown>;
    @ViewChild('signupForm', { read: ElementRef })
    signupFormRef?: ElementRef<HTMLFormElement>;

    templateMap: { [key: string]: TemplateRef<any> } = {};

    @select(['application', 'highContrast']) highContrast$: Observable<boolean>;

    protected hasRecaptcha = false;
    public registrationOptions = this.env.REGISTRATION;
    private _terms = null;
    public _registerForm: FormGroup;
    assetsPath$ = this.applicationFacade.assetsPath$;

    public _termsAgreed = false;
    private _personalDataAgreed = false;
    private _gdprAgreed = false;
    termsUrl: string;

    tokenVisible = false;
    siteKey: string;
    recaptchaValue = '';
    hide = true;
    hideConfirm = true;
    @Input() captchaToggleAllowed = false;
    @Input() options: WidgetOptionsForEmailRegister;
    @Output() didRegister = new EventEmitter<RegisterWithEmailDTO>();
    fieldOrderKeys: string[];

    constructor(
        @Inject('ENV') public env: Env,
        private ngRedux: NgRedux<any>,
        private notification: NotificationFacade,
        private applicationFacade: ApplicationFacade,
        private liveAnnouncer: LiveAnnouncer,
        private translate: TranslateService,
        private savePasswordService: SavePasswordService,
        private matDialog: MatDialog,
        private keyboardAssistService: KeyboardAssistService,
        private featureFlagService: LaunchDarklyService
    ) {
        super();
        if (this.env.REGISTRATION?.FIELD_ORDER) {
            this.fieldOrderKeys = Object.keys(this.env.REGISTRATION.FIELD_ORDER);
        }
    }

    private detachKeyboardAssist?: () => void;

    ngOnInit() {
        this.initHasRecaptcha();

        this._terms = this.ngRedux.getState().core.region.terms;
        this.syncTermsUrl();
        this.translate.onLangChange
            .pipe(
                takeUntil(this.componentDestroyed$),
                switchMap((event: LangChangeEvent) => {
                    this.options.locale = event.lang;
                    return this.highContrast$.pipe(take(1));
                })
            )
            .subscribe((isHighContrastEnabled: boolean) => {
                this.setTermsUrl(isHighContrastEnabled, this.resolveTermsLocale(this.options?.locale));
            });
        const options: { [key: string]: AbstractControl } = {
            email: new FormControl('', [
                !this.env.REGISTRATION?.FIELD_ORDER?.EMAIL?.OPTIONAL ? Validators.required : () => null,
                Validators.email
            ]),
            name: new FormControl(
                '',
                !!this.registrationOptions.FULL_NAME_IS_OPTIONAL ||
                !!this.env.REGISTRATION?.FIELD_ORDER?.FIRST_NAME?.OPTIONAL
                    ? []
                    : [Validators.required]
            ),
            lastName: new FormControl('', [
                !!this.registrationOptions.FULL_NAME_IS_OPTIONAL ||
                !!this.env.REGISTRATION?.FIELD_ORDER?.LAST_NAME?.OPTIONAL
                    ? () => null
                    : Validators.required
            ]),
            password: new FormControl('', passwordValidator),
            confirmPassword: new FormControl('', [Validators.required, valueMatchValidator('password')]),
            terms: new FormControl(this._termsAgreed, Validators.required),
            gdpr: new FormControl(this._gdprAgreed, Validators.required)
        };

        if (this.env.REGISTRATION?.PERSONAL_DATA) {
            options.personalData = new FormControl(this._personalDataAgreed, Validators.required);
        }
        if (this.env.REGISTRATION?.FIELD_ORDER) {
            if (this.fieldOrderKeys.includes('PHONE_NUMBER')) {
                options.phoneNumber = new FormControl(
                    this.env?.REGISTRATION?.USE_COUNTRY_CODE_ON_FORMS ? this.env?.DEFAULT_PHONE_COUNTRY_CODE ?? '' : '',
                    [
                        !this.env.REGISTRATION?.FIELD_ORDER?.PHONE_NUMBER?.OPTIONAL ? Validators.required : () => null,
                        phoneValidNumberValidator
                    ]
                );
            }

            if (this.fieldOrderKeys.includes('PERSONAL_CODE')) {
                const personalCodePattern = this.getPersonalCodeValidationPattern();
                options.personalCode = new FormControl('', [
                    !this.env.REGISTRATION?.FIELD_ORDER?.PERSONAL_CODE?.OPTIONAL ? Validators.required : () => null,
                    Validators.pattern(personalCodePattern)
                ]);
            }
        }

        this._registerForm = new FormGroup(options);

        if (!this.registrationOptions.CHECK_GDPR) {
            this._registerForm.removeControl('gdpr');
        }

        if (!this.registrationOptions.DISPLAY_FULL_NAME_FIELD) {
            this._registerForm.removeControl('name');
            this._registerForm.removeControl('lastName');
        }

        if (this.env?.RECAPTCHA?.SITEKEY) {
            this.siteKey = this.env.RECAPTCHA.SITEKEY;
        }
    }

    ngAfterViewInit() {
        if (this.env.REGISTRATION?.FIELD_ORDER) {
            Object.keys(this.env.REGISTRATION.FIELD_ORDER).forEach((fieldKey) => {
                this.templateMap[fieldKey] = this[fieldKey];
            });
        }

        const formElement = this.signupFormRef?.nativeElement;
        if (formElement) {
            this.detachKeyboardAssist = this.keyboardAssistService.attachTo(formElement);
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.detachKeyboardAssist?.();
    }

    syncTermsUrl() {
        this.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isHighContrastEnabled: boolean) => {
            this.setTermsUrl(isHighContrastEnabled);
        });
    }

    setTermsUrl(isHighContrastEnabled: boolean, locale = this.resolveTermsLocale(this.options?.locale)) {
        if (!this._terms) {
            return;
        }

        if (this.env.ENABLE_TERMS_WMB_VERSION) {
            this.termsUrl = this.getTermsUrl(isHighContrastEnabled, this._terms.contentBaseUrl, locale);
        } else {
            this.termsUrl = this.getTermsUrl(isHighContrastEnabled, this._terms.content_base_url, locale);
        }
    }

    private resolveTermsLocale(fallbackLocale?: string): string {
        return this.translate.currentLang || localStorage.getItem('LOCALIZE_DEFAULT_LANGUAGE') || fallbackLocale;
    }

    toggleCheckbox(name: string) {
        if (name === 'terms') {
            this._termsAgreed = !this._termsAgreed;
            if (this._termsAgreed) {
                this.notification.removeAll();
            }
            this._registerForm.controls['terms'].patchValue(this._termsAgreed);
        }
        if (name === 'gdpr') {
            this._gdprAgreed = !this._gdprAgreed;
            this._registerForm.controls['gdpr'].patchValue(this._gdprAgreed);
        }

        if (name === 'personalData') {
            this._personalDataAgreed = !this._personalDataAgreed;
            this._registerForm.controls['personalData'].patchValue(this._personalDataAgreed);
        }
    }

    get showGreenCheck() {
        return this.env.APP_NAME === 'gotland';
    }

    agreeTerms(agreement: boolean) {
        this._termsAgreed = agreement;
        this._registerForm.controls['terms'].patchValue(this._termsAgreed);
    }

    togglePassword(event: MouseEvent = null) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.liveAnnouncer.announce(this.translate.instant(this.hide ? 'PASSWORD_IS_VISIBLE' : 'PASSWORD_IS_HIDDEN'));
        this.hide = !this.hide;
    }

    toggleConfirmPassword(event: MouseEvent = null) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.liveAnnouncer.announce(
            this.translate.instant(this.hideConfirm ? 'CONFIRM_PASSWORD_IS_VISIBLE' : 'CONFIRM_PASSWORD_IS_HIDDEN')
        );
        this.hideConfirm = !this.hideConfirm;
    }

    getTermsUrl(highContrast: boolean = false, baseUrl: string, locale: string) {
        let termsUrl = `${baseUrl}/${locale}/`;
        if (highContrast) {
            termsUrl += '#high-contrast';
        }
        return termsUrl;
    }

    register() {
        if (!this._termsAgreed && this.registrationOptions.CHECK_GDPR && !this._gdprAgreed) {
            this.notification.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
        } else {
            if (!this._termsAgreed && this._terms) {
                this.notification.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
            } else {
                this.notification.removeAll();
            }

            if (this.registrationOptions.CHECK_GDPR && !this._gdprAgreed) {
                this.notification.error(this.translate.instant('MUST_AGREE_GDPR'));
            }
        }

        if (this.env.REGISTRATION?.PERSONAL_DATA && !this._personalDataAgreed) {
            this.notification.error(this.translate.instant('MUST_AGREE_PERSONAL_DATA'));
        }

        const password = this._registerForm.controls.password.value;

        if (password !== this._registerForm.controls.confirmPassword.value) {
            this._registerForm.controls.confirmPassword.reset(this._registerForm.controls.confirmPassword.value);
        }
        if (this._registerForm.valid) {
            const payload = this._registerForm.getRawValue();
            payload.terms = this._terms;
            delete payload['gdpr'];

            if (this.env.REGISTRATION?.DIVIDED_FIRST_AND_LAST_NAME && this.env.REGISTRATION?.DISPLAY_FULL_NAME_FIELD) {
                payload.firstName = payload.name;
                payload.lastName = payload.lastName || '';
                delete payload['name'];
            }

            const email = this._registerForm.controls.email.value;
            this.savePasswordService.promptSaveCredentials(email, password);
            this.didRegister.emit(payload);
        } else {
            this._registerForm.markAllAsTouched();
        }
    }

    resolved(captchaResponse: string): void {
        this.recaptchaValue = captchaResponse;
    }

    openTermsModalClick(): void {
        const termsDialog = this.matDialog.open(TermsDialogMobileComponent, {
            data: { termsUrl: this.termsUrl, withAgreeButton: true },
            autoFocus: true,
            width: '90%',
            maxWidth: '80vh',
            panelClass: 'terms-dialog-mobile-wrapper'
        });

        termsDialog
            .afterClosed()
            .pipe(take(1))
            .subscribe((termsAgreed) => this.agreeTerms(termsAgreed));
    }

    getFirstError(control: AbstractControl, errorCodes: string[]): string {
        return errorCodes.find((errorCode) => control.hasError(errorCode));
    }

    isRegisterButtonDisabled() {
        return (
            (!this.env.REGISTRATION.ENABLE_REGISTER_BUTTON &&
                (!this._registerForm.valid || (!this.recaptchaValue && this.hasRecaptcha))) ||
            (this.env.REGISTRATION?.DISABLE_REGISTER_BUTTON_TERMS_NOT_CHECKED && !this._termsAgreed)
        );
    }

    /**
     * Ensures that when a form field receives focus on mobile devices,
     * it scrolls into view to avoid being obscured by the virtual keyboard.
     * @param event FocusEvent triggered when an element gains focus.
     */
    @HostListener('focusin', ['$event'])
    handleFocusIn(event: FocusEvent) {
        const target = event.target as HTMLElement;
        const tagName = target?.tagName?.toLowerCase();
        if (!tagName || !['input', 'textarea', 'select'].includes(tagName)) {
            return;
        }

        // Ensure the focused control is visible when the keyboard opens on mobile.
        requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    private initHasRecaptcha(): void {
        this.featureFlagService
            .getFlags(FEATURE_FLAGS.FrontendRecaptcha)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((flagValue: boolean) => (this.hasRecaptcha = flagValue || !this.captchaToggleAllowed));
    }

    private getPersonalCodeValidationPattern(): RegExp {
        const regexFromField = this.env.REGISTRATION?.FIELD_ORDER?.PERSONAL_CODE?.VALIDATION?.REGEXP;
        const regexSource = regexFromField || String.raw`^\d{12}$`;

        try {
            return new RegExp(regexSource);
        } catch {
            return /^\d{12}$/;
        }
    }
}
